import { untimedProblem, codeProblemOf } from '../../domain/codeStatus';
import type { CodeProblem } from '../../domain/codeStatus';
import { formProblemOf, isIdentifierRefusal } from '../../domain/refusal';
import type { FormProblem, Refusal } from '../../domain/refusal';
import type { IdentifierKind, IdentifyResult } from '../../types/domain.types';

export interface LoginFailures {
  readonly field: Refusal | null;
  readonly password: Refusal | null;
  readonly form: FormProblem | null;
}

export const NO_FAILURES: LoginFailures = { field: null, password: null, form: null };

const HANDLED_BY_STEP: readonly string[] = ['LOCKED', 'NO_PASSWORD_SET', 'USE_CODE', 'TOO_MANY_SENDS', 'RESEND_TOO_SOON'];

const handledIn = (codes: readonly string[], refusal: Refusal): boolean => refusal.code !== null && codes.includes(refusal.code);

export const errorOf = <T extends { readonly epoch: number }>(
  mutation: { readonly error: Error | null; readonly variables: T | undefined },
  epoch: number,
): Error | null => (mutation.error !== null && mutation.variables?.epoch === epoch ? mutation.error : null);

export const fieldFailures = (error: Error | null): LoginFailures => {
  if (error === null) return NO_FAILURES;
  const problem = formProblemOf(error);
  if (problem.kind === 'refused' && isIdentifierRefusal(problem.refusal)) return { ...NO_FAILURES, field: problem.refusal };
  if (problem.kind === 'refused' && handledIn(HANDLED_BY_STEP, problem.refusal)) return NO_FAILURES;
  return { ...NO_FAILURES, form: problem };
};

export const passwordFailures = (error: Error | null): LoginFailures => {
  if (error === null) return NO_FAILURES;
  const problem = formProblemOf(error);
  if (problem.kind === 'refused' && problem.refusal.code === 'WRONG_PASSWORD') return { ...NO_FAILURES, password: problem.refusal };
  return fieldFailures(error);
};

export const resendProblemOf = (sendError: Error | null): CodeProblem | null => untimedProblem(codeProblemOf(sendError));

export const unverifiedKindOf = (found: IdentifyResult | undefined): IdentifierKind | null => {
  if (found === undefined) return null;
  if (found.kind === 'PHONE' && !found.phoneVerified) return 'PHONE';
  if (found.kind === 'EMAIL' && !found.emailVerified) return 'EMAIL';
  return null;
};
